import React, { useCallback, useEffect, useState } from "react";
import { Copy, Check, X, ExternalLink, Radio as RadioIcon, RefreshCw, Loader2, AlertCircle } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

/**
 * BridgeToObsModal — one-click wizard that fetches the host's persistent
 * Cloudflare RTMPS ingest credentials and walks them through the exact
 * OBS configuration steps needed to broadcast their Spotify (or any
 * system-audio) output to Tap In TV listeners.
 *
 * Why it exists: Spotify audio is DRM-protected and can't be piped
 * through the in-browser EQ/FX/recorder. The only legal route to push
 * Spotify to listeners is OBS → Cloudflare RTMP. This modal turns that
 * multi-step manual setup into a copy-paste wizard.
 *
 * Endpoints used:
 *   GET  /api/live/me/channel-key        — fetch persistent RTMPS url+key
 *   POST /api/live/me/channel-key/rotate — burn + reprovision (security)
 *
 * Props:
 *   open      bool          whether the modal is visible
 *   onClose   () => void    close handler
 */
export default function BridgeToObsModal({ open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState({});
  const [rotating, setRotating] = useState(false);
  const [revealKey, setRevealKey] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: d } = await api.get("/live/me/channel-key");
      setData(d);
    } catch (e) {
      setError(formatApiError(e) || "Couldn't load your stream key.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && !data) load();
  }, [open, data, load]);

  const copy = async (key, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied((c) => ({ ...c, [key]: true }));
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied((c) => ({ ...c, [key]: false })), 1800);
    } catch {
      toast.error("Couldn't copy — your browser blocked clipboard access.");
    }
  };

  const rotate = async () => {
    if (!window.confirm("Rotate your stream key? Your current OBS setup will stop working until you paste the new key.")) return;
    setRotating(true);
    try {
      const { data: d } = await api.post("/live/me/channel-key/rotate");
      // Re-fetch so we get the full payload (the rotate response only
      // returns the new url+key, not all the other fields the wizard
      // uses).
      await load();
      toast.success("New stream key provisioned.");
    } catch (e) {
      toast.error(formatApiError(e) || "Rotation failed — try again.");
    } finally {
      setRotating(false);
    }
  };

  if (!open) return null;

  const configured = data?.configured;
  const url = data?.rtmps_url || "";
  const key = data?.rtmps_stream_key || "";
  const maskedKey = key ? `${key.slice(0, 4)}${"•".repeat(Math.max(0, key.length - 8))}${key.slice(-4)}` : "";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="bridge-to-obs-title"
      data-testid="bridge-to-obs-modal"
      className="fixed inset-0 z-[80] flex items-end md:items-center justify-center bg-black/80 backdrop-blur-sm p-0 md:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="bg-zinc-900 border border-white/10 rounded-t-2xl md:rounded-2xl w-full md:max-w-xl max-h-[92vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10 sticky top-0 bg-zinc-900 z-10">
          <div className="w-9 h-9 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
            <RadioIcon className="w-4 h-4 text-emerald-300" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 id="bridge-to-obs-title" className="font-display font-black text-white text-base md:text-lg leading-tight">
              Bridge to OBS · Broadcast Spotify
            </h2>
            <p className="text-white/60 text-[11px] md:text-xs leading-snug">
              Stream your system audio (Spotify, Tidal, anything) to Tap In TV listeners via Cloudflare RTMP.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="bridge-to-obs-close"
            aria-label="Close"
            className="text-white/60 hover:text-white p-1 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-5 space-y-5">
          {loading && (
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Fetching your stream key…
            </div>
          )}

          {error && !loading && (
            <div className="bg-red-500/10 border border-red-500/30 rounded p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-300 shrink-0 mt-0.5" />
              <div className="text-red-100 text-sm">{error}</div>
            </div>
          )}

          {!loading && data && !configured && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded p-3 text-amber-100 text-sm">
              {data.message || "Cloudflare Stream isn't configured on this server yet. Ask the admin to add the API keys before bridging from OBS."}
            </div>
          )}

          {!loading && configured && (
            <>
              {/* Step 1 */}
              <section>
                <div className="text-tv-accent text-[11px] font-black uppercase tracking-widest mb-2">Step 1 · Open OBS</div>
                <p className="text-white/80 text-sm leading-snug">
                  In OBS, go to <strong className="text-white">Settings → Stream</strong>. Set <strong className="text-white">Service: Custom…</strong> Then paste the values below.
                </p>
              </section>

              {/* Step 2 - Server URL */}
              <section>
                <div className="text-tv-accent text-[11px] font-black uppercase tracking-widest mb-2">Step 2 · Server URL</div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={url}
                    data-testid="bridge-obs-server-url"
                    className="flex-1 bg-black/40 border border-white/10 rounded px-3 py-2 text-white text-xs md:text-sm font-mono outline-none focus:border-tv-accent"
                    onFocus={(e) => e.target.select()}
                  />
                  <button
                    type="button"
                    onClick={() => copy("url", url)}
                    data-testid="bridge-obs-copy-url"
                    className="px-3 py-2 bg-tv-accent hover:bg-yellow-400 text-black font-bold text-xs rounded inline-flex items-center gap-1.5"
                  >
                    {copied.url ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied.url ? "Copied" : "Copy"}
                  </button>
                </div>
              </section>

              {/* Step 3 - Stream Key */}
              <section>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-tv-accent text-[11px] font-black uppercase tracking-widest">Step 3 · Stream Key</div>
                  <button
                    type="button"
                    onClick={() => setRevealKey((v) => !v)}
                    data-testid="bridge-obs-reveal-key"
                    className="text-[10px] uppercase tracking-wider text-white/60 hover:text-white font-bold"
                  >
                    {revealKey ? "Hide" : "Reveal"}
                  </button>
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={revealKey ? key : maskedKey}
                    data-testid="bridge-obs-stream-key"
                    className="flex-1 bg-black/40 border border-white/10 rounded px-3 py-2 text-white text-xs md:text-sm font-mono outline-none focus:border-tv-accent"
                    onFocus={(e) => e.target.select()}
                  />
                  <button
                    type="button"
                    onClick={() => copy("key", key)}
                    data-testid="bridge-obs-copy-key"
                    className="px-3 py-2 bg-tv-accent hover:bg-yellow-400 text-black font-bold text-xs rounded inline-flex items-center gap-1.5"
                  >
                    {copied.key ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied.key ? "Copied" : "Copy"}
                  </button>
                </div>
                <p className="text-amber-300/80 text-[10px] mt-1.5 leading-snug">
                  Treat this key like a password. If you suspect it was exposed, rotate it below.
                </p>
              </section>

              {/* Step 4 - Add system audio */}
              <section>
                <div className="text-tv-accent text-[11px] font-black uppercase tracking-widest mb-2">Step 4 · Add System Audio</div>
                <ol className="list-decimal list-inside text-white/80 text-sm space-y-1.5 leading-snug">
                  <li>In OBS → <strong className="text-white">Sources</strong> → click <strong className="text-white">+</strong> → pick:
                    <ul className="list-disc list-inside ml-4 mt-0.5 text-white/70 text-[13px]">
                      <li><strong>Windows:</strong> "Audio Output Capture" (Desktop Audio)</li>
                      <li><strong>macOS:</strong> install <a className="text-tv-accent underline" href="https://github.com/ExistentialAudio/BlackHole" target="_blank" rel="noreferrer">BlackHole 2ch</a>, then add "Audio Input Capture"</li>
                    </ul>
                  </li>
                  <li>Optional: add a Video Source so listeners on TV see something (Logo, Image, or your Tap In channel page in a Browser Source).</li>
                </ol>
              </section>

              {/* Step 5 - Go Live */}
              <section>
                <div className="text-tv-accent text-[11px] font-black uppercase tracking-widest mb-2">Step 5 · Start Streaming</div>
                <p className="text-white/80 text-sm leading-snug">
                  Hit <strong className="text-white">Start Streaming</strong> in OBS. Play Spotify on your computer — listeners will hear it on <strong className="text-white">tapintvnetwork.com/live/[your-channel]</strong> within ~5 seconds.
                </p>
              </section>

              {/* Footer actions */}
              <div className="pt-3 border-t border-white/10 flex flex-wrap gap-2 justify-between items-center">
                <a
                  href="https://obsproject.com/download"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-tv-accent hover:text-yellow-300 text-xs font-bold"
                  data-testid="bridge-obs-download-link"
                >
                  <ExternalLink className="w-3 h-3" /> Don't have OBS yet?
                </a>
                <button
                  type="button"
                  onClick={rotate}
                  disabled={rotating}
                  data-testid="bridge-obs-rotate-key"
                  className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-full border ${rotating ? "bg-white/5 text-white/40 border-white/10 cursor-wait" : "bg-red-500/15 hover:bg-red-500/25 text-red-200 border-red-500/40"}`}
                  title="Burn the current key and provision a new one"
                >
                  {rotating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                  Rotate key
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
